import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { COURSES_QUERY_KEY } from '@/features/courses/useCourses';
import { courseQueryKey, type RawComponent } from '@/features/courses/useCourse';

import { copyComponent } from './componentService';

export interface CopyComponentMutationInput {
  component: RawComponent;
  toCourseId: string;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function useCopyComponent(sourceCourseId: string | undefined) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, CopyComponentMutationInput>({
    mutationFn: async (params) => {
      if (!user) {
        throw createDataError('You must be signed in to copy a chapter.');
      }

      const toCourseId = params.toCourseId.trim();

      if (!toCourseId) {
        throw createDataError('Select a course before copying this chapter.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      await copyComponent(client, {
        component: params.component,
        toCourseId,
      });
    },
    onSuccess: async (_data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: COURSES_QUERY_KEY }),
      ];

      if (sourceCourseId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: courseQueryKey(sourceCourseId),
          }),
        );
      }

      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: courseQueryKey(variables.toCourseId),
        }),
      );

      await Promise.all(invalidations);
    },
  });
}
